import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { Layout } from '@/components/layout/Layout'
import { LandingPage } from '@/pages/landing/LandingPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage'
import { ProductsPage } from '@/pages/products/ProductsPage'
import { ProductCreatePage } from '@/pages/products/ProductCreatePage'
import { ProductDetailPage } from '@/pages/products/ProductDetailPage'
import { LibraryPage } from '@/pages/library/LibraryPage'
import { CompetitorResearchPage } from '@/pages/generate/CompetitorResearchPage'
import { VariationGeneratePage } from '@/pages/generate/VariationGeneratePage'
import { ImageFreestylePage } from '@/pages/generate/ImageFreestylePage'
import { VideoFreestylePage } from '@/pages/generate/VideoFreestylePage'
import { DigitalHumanPage } from '@/pages/generate/DigitalHumanPage'
import { IdeasPage } from '@/pages/creative-lab/IdeasPage'
import { VerticalIdeasPage } from '@/pages/creative-lab/VerticalIdeasPage'
import { TasksPage } from '@/pages/TasksPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { SubscriptionPage } from '@/pages/settings/SubscriptionPage'
import { BillingPage } from '@/pages/settings/BillingPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { FeedbackPage } from '@/pages/finance/FeedbackPage'
import { FeedbackOverviewPage } from '@/pages/finance/FeedbackOverviewPage'
import { MaterialAnalyticsPage } from '@/pages/analytics/MaterialAnalyticsPage'
import { FinanceReportPage } from '@/pages/analytics/FinanceReportPage'
import { DramaProjectsPage } from '@/pages/drama/DramaProjectsPage'
import { DramaProjectDetailPage } from '@/pages/drama/DramaProjectDetailPage'
import { DailyReviewListPage } from '@/pages/internal/DailyReviewListPage'
import { DailyReviewBatchPage } from '@/pages/internal/DailyReviewBatchPage'
import { Toaster } from '@/components/ui/toaster'
import { TaskProgressToast } from '@/components/tasks/TaskProgressToast'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  const isEmbedded = window.self !== window.top

  if (!isAuthenticated) {
    if (isEmbedded) {
      return (
        <EmbeddedAuthGate>
          {children}
        </EmbeddedAuthGate>
      )
    }
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function EmbeddedAuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  const [timedOut, setTimedOut] = React.useState(false)

  React.useEffect(() => {
    window.parent.postMessage({ type: 'REQUEST_TOKEN' }, '*')
    const t = setTimeout(() => setTimedOut(true), 5000)
    return () => clearTimeout(t)
  }, [])

  if (isAuthenticated) return <>{children}</>
  if (timedOut) return <Navigate to="/login" replace />
  return (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}

function EmailGate({ page, children }: { page: string; children: React.ReactNode }) {
  const { allowedPages } = useAuthStore()

  // null = page-access not yet loaded; wait instead of redirecting prematurely
  if (allowedPages === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!allowedPages.includes(page)) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}

function InternalRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthStore()

  // Profile not loaded yet — don't redirect before we know is_internal
  if (isAuthenticated && !user) {
    return null
  }

  if (!user?.is_internal) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}

function OnboardingCheck({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  const isEmbedded = window.self !== window.top

  if (!isEmbedded && user && !user.is_onboarded) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

function App() {
  const { isAuthenticated } = useAuthStore()

  return (
    <>
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Onboarding (protected but no sidebar) */}
        <Route
          path="/onboarding"
          element={
            <PrivateRoute>
              <OnboardingPage />
            </PrivateRoute>
          }
        />

        {/* Protected routes with layout */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <OnboardingCheck>
                <Layout>
                  <Routes>
                    {/* Overview */}
                    <Route path="/home" element={<DashboardPage />} />

                    {/* Generate - Image */}
                    <Route path="/generate/image/competitor" element={<CompetitorResearchPage />} />
                    <Route path="/generate/image/variation" element={<VariationGeneratePage />} />
                    <Route path="/generate/image/freestyle" element={<ImageFreestylePage />} />

                    {/* Generate - Video */}
                    <Route path="/generate/video/freestyle" element={<VideoFreestylePage />} />
                    <Route path="/generate/video/digital-human" element={<DigitalHumanPage />} />

                    {/* Creative Lab */}
                    <Route path="/creative-lab/ideas" element={<IdeasPage />} />
                    <Route path="/creative-lab/vertical-ideas" element={<VerticalIdeasPage />} />
                    <Route path="/creative-lab/foreplay" element={<Navigate to="/creative-lab/ideas?tab=ads" replace />} />
                    <Route path="/creative-lab/perplexity" element={<Navigate to="/creative-lab/ideas?tab=web" replace />} />

                    {/* Products */}
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/new" element={<ProductCreatePage />} />
                    <Route path="/products/:id" element={<ProductDetailPage />} />

                    {/* Library */}
                    <Route path="/library" element={<LibraryPage />} />

                    {/* Drama Mix */}
                    <Route path="/drama" element={<DramaProjectsPage />} />
                    <Route path="/drama/:projectId" element={<DramaProjectDetailPage />} />

                    {/* Tasks */}
                    <Route path="/tasks" element={<TasksPage />} />

                    {/* Analytics (email-whitelisted) */}
                    <Route
                      path="/analytics/materials"
                      element={
                        <EmailGate page="material_analytics">
                          <MaterialAnalyticsPage />
                        </EmailGate>
                      }
                    />
                    <Route
                      path="/analytics/finance-report"
                      element={
                        <EmailGate page="material_analytics">
                          <FinanceReportPage />
                        </EmailGate>
                      }
                    />

                    {/* Internal: daily batch review */}
                    <Route
                      path="/internal/daily-review"
                      element={
                        <InternalRoute>
                          <DailyReviewListPage />
                        </InternalRoute>
                      }
                    />
                    <Route
                      path="/internal/daily-review/:batchId"
                      element={
                        <InternalRoute>
                          <DailyReviewBatchPage />
                        </InternalRoute>
                      }
                    />

                    {/* Image variants (email-whitelisted): 总览 -> 标注详情 */}
                    <Route
                      path="/finance/feedback"
                      element={
                        <EmailGate page="finance">
                          <FeedbackOverviewPage />
                        </EmailGate>
                      }
                    />
                    <Route
                      path="/finance/feedback/review"
                      element={
                        <EmailGate page="finance">
                          <FeedbackPage />
                        </EmailGate>
                      }
                    />

                    {/* Settings */}
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/subscription" element={<SubscriptionPage />} />
                    <Route path="/settings/billing" element={<BillingPage />} />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/home" replace />} />
                  </Routes>
                </Layout>
              </OnboardingCheck>
            </PrivateRoute>
          }
        />
      </Routes>
      <Toaster />
      {/* Show task progress toast only for authenticated users */}
      {isAuthenticated && <TaskProgressToast />}
    </>
  )
}

export default App
